import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContextTest'
import { 
  Users, 
  Ruler,
  FileText,
  CreditCard as CurrencyIcon,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  ArrowRight,
  Calendar,
  CreditCard,
  Filter,
  Activity,
  BarChart3,
  PieChart
} from 'lucide-react'
import { LoadingSpinner } from '../components'
import { dashboardService } from '../services/dashboardService'
import toast from 'react-hot-toast'

function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState(null)
  const [period, setPeriod] = useState('month')
  const [limit, setLimit] = useState(5)

  const periods = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' }
  ]

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true)
        
        // Try to fetch from dashboard endpoint first, fallback to individual endpoints
        try {
          const response = await dashboardService.getDashboard({ period, limit })
          console.log('Dashboard API Response:', response)
          
          // Handle response structure: 
          // API returns: { success: true, data: { stats, recentActivity, charts } }
          // dashboardService returns: response.data which is the above object
          // So we need response.data to get the inner data object
          const data = response.data || response
          console.log('Dashboard Data:', data)
          setDashboardData(data)
        } catch (error) {
          // Fallback: fetch from individual endpoints
          console.log('Dashboard endpoint not available, using fallback')
          const [statsRes, activityRes, chartsRes] = await Promise.all([
            dashboardService.getStats({ period }).catch(() => ({ data: null })),
            dashboardService.getActivity({ limit }).catch(() => ({ data: null })),
            dashboardService.getCharts({ period }).catch(() => ({ data: null }))
          ])

          setDashboardData({
            stats: statsRes.data?.stats || statsRes.stats || null,
            recentActivity: activityRes.data?.activity || activityRes.activity || null,
            charts: chartsRes.data?.charts || chartsRes.charts || null
          })
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        toast.error('Failed to load dashboard data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardData()
  }, [period, limit])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-secondary-500">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // Extract data from response structure
  const stats = dashboardData?.stats || dashboardData?.data?.stats || {}
  const recentActivity = dashboardData?.recentActivity || dashboardData?.data?.recentActivity || {}
  const charts = dashboardData?.charts || dashboardData?.data?.charts || {}

  const statsCards = [
    {
      name: 'Total Customers',
      value: stats.totalCustomers?.value || 0,
      periodValue: stats.totalCustomers?.periodValue || 0,
      change: { 
        value: stats.totalCustomers?.change || 0, 
        type: (stats.totalCustomers?.change || 0) >= 0 ? 'positive' : 'negative' 
      },
      icon: Users,
      gradient: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      link: '/customers'
    },
    {
      name: 'Total Measurements',
      value: stats.totalMeasurements?.value || 0,
      periodValue: stats.totalMeasurements?.periodValue || 0,
      change: { 
        value: stats.totalMeasurements?.change || 0, 
        type: (stats.totalMeasurements?.change || 0) >= 0 ? 'positive' : 'negative' 
      },
      icon: Ruler,
      gradient: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      link: '/measurements'
    },
    {
      name: 'Total Invoices',
      value: stats.totalInvoices?.value || 0,
      periodValue: stats.totalInvoices?.periodValue || 0,
      change: { 
        value: stats.totalInvoices?.change || 0, 
        type: (stats.totalInvoices?.change || 0) >= 0 ? 'positive' : 'negative' 
      },
      icon: FileText,
      gradient: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      link: '/invoices'
    },
    {
      name: 'Total Revenue',
      value: stats.totalRevenue?.formatted || `₹${(stats.totalRevenue?.value || 0).toLocaleString('en-IN')}`,
      periodValue: stats.totalRevenue?.periodValue ? `₹${stats.totalRevenue.periodValue.toLocaleString('en-IN')}` : null,
      change: { 
        value: stats.totalRevenue?.change || 0, 
        type: (stats.totalRevenue?.change || 0) >= 0 ? 'positive' : 'negative' 
      },
      icon: CurrencyIcon,
      gradient: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      link: '/invoices'
    },
    {
      name: 'Pending Invoices',
      value: stats.pendingInvoices?.value || 0,
      pendingAmount: stats.pendingInvoices?.formattedAmount || `₹${(stats.pendingInvoices?.pendingAmount || 0).toLocaleString('en-IN')}`,
      change: { 
        value: stats.pendingInvoices?.change || 0, 
        type: (stats.pendingInvoices?.change || 0) >= 0 ? 'positive' : 'negative' 
      },
      icon: Clock,
      gradient: 'from-yellow-500 to-yellow-600',
      bgColor: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      link: '/invoices?status=Pending'
    },
    {
      name: 'Paid Invoices',
      value: stats.paidInvoices?.value || 0,
      change: { 
        value: stats.paidInvoices?.change || 0, 
        type: (stats.paidInvoices?.change || 0) >= 0 ? 'positive' : 'negative' 
      },
      icon: CheckCircle,
      gradient: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      link: '/invoices?status=Paid'
    },
  ]

  const recentCustomers = recentActivity.recentCustomers || []
  const recentMeasurements = recentActivity.recentMeasurements || []
  const recentInvoices = recentActivity.recentInvoices || []

  // Chart data - API returns values array, not data array
  const revenueChartData = charts.revenue ? {
    labels: charts.revenue.labels || [],
    data: charts.revenue.values || charts.revenue.data || []
  } : { labels: [], data: [] }
  
  // Invoice status chart - create from stats if charts.invoices is not available
  let invoiceStatusData = []
  if (charts.invoices && Array.isArray(charts.invoices)) {
    invoiceStatusData = charts.invoices
  } else if (charts.invoices && charts.invoices.labels && charts.invoices.values) {
    // Convert labels and values arrays to object array
    invoiceStatusData = charts.invoices.labels.map((label, index) => ({
      label: label,
      value: charts.invoices.values[index] || 0
    }))
  } else {
    // Fallback: create from stats data
    invoiceStatusData = [
      { label: 'Pending', value: stats.pendingInvoices?.value || 0 },
      { label: 'Paid', value: stats.paidInvoices?.value || 0 },
      { label: 'Partial', value: 0 }, // Add if available in stats
      { label: 'Overdue', value: 0 } // Add if available in stats
    ].filter(item => item.value > 0) // Only show statuses with values
  }
  
  const customerGrowthData = charts.customerGrowth || { labels: [], data: [] }

  // Calculate invoice status percentages for pie chart
  const totalInvoicesForChart = invoiceStatusData.reduce((sum, item) => sum + (item.value || 0), 0)
  const invoiceStatusWithPercentages = invoiceStatusData.map(item => ({
    ...item,
    percentage: totalInvoicesForChart > 0 ? (((item.value || 0) / totalInvoicesForChart) * 100).toFixed(1) : 0
  }))

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-secondary-50">
      <div className="space-y-8 pb-8">
        {/* Header Section */}
        <div className="bg-white border-b border-secondary-200 shadow-sm">
          <div className="px-6 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
                <h1 className="text-4xl font-bold text-secondary-900">Dashboard</h1>
                <p className="mt-2 text-base text-secondary-600">
                  Welcome back, <span className="font-semibold text-primary-600">{user?.firstName}</span>! Here's your business overview.
        </p>
      </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex items-center space-x-2 bg-secondary-50 rounded-lg px-3 py-2 border border-secondary-200">
                  <Filter className="h-4 w-4 text-secondary-500" />
                  <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    className="bg-transparent border-0 text-sm font-medium text-secondary-700 focus:outline-none focus:ring-0 cursor-pointer"
                  >
                    {periods.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2 text-sm text-secondary-600 bg-secondary-50 rounded-lg px-3 py-2 border border-secondary-200">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                </div>
              </div>
            </div>
          </div>
      </div>

        <div className="px-6 space-y-8">
          {/* Stats Grid - Enhanced Design */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {statsCards.map((stat) => (
              <div 
                key={stat.name} 
                className="group relative bg-white rounded-xl border border-secondary-200 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                onClick={() => stat.link && navigate(stat.link)}
              >
                {/* Gradient accent bar */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.gradient}`}></div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl ${stat.bgColor} group-hover:scale-110 transition-transform duration-300`}>
                      <stat.icon className={`h-6 w-6 ${stat.iconColor}`} />
          </div>
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${
                      stat.change?.type === 'positive' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {stat.change?.type === 'positive' ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      <span>{stat.change?.value > 0 ? '+' : ''}{stat.change?.value || 0}%</span>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-secondary-500 uppercase tracking-wide">
                      {stat.name}
                    </p>
                    <p className="text-3xl font-bold text-secondary-900">
                      {stat.value}
                    </p>
                    {stat.periodValue && (
                      <p className="text-sm text-secondary-500 flex items-center">
                        <Activity className="h-3 w-3 mr-1" />
                        {stat.periodValue} this {period === 'today' ? 'day' : period}
                      </p>
                    )}
                    {stat.pendingAmount && (
                      <p className="text-sm font-medium text-secondary-700 mt-2">
                        Amount: {stat.pendingAmount}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Section - Enhanced */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Revenue Chart */}
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-emerald-50 to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2 text-emerald-600" />
                      Revenue Trend
                    </h3>
                    <p className="text-sm text-secondary-500 mt-1">Daily revenue breakdown</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {revenueChartData.data && revenueChartData.data.length > 0 ? (
                  <div className="h-72 flex items-end justify-between space-x-1">
                    {revenueChartData.data.map((value, index) => {
                      const maxValue = Math.max(...revenueChartData.data)
                      const height = maxValue > 0 ? (value / maxValue) * 100 : 0
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center group">
                          <div className="w-full relative" style={{ height: '240px' }}>
                            <div 
                              className="absolute bottom-0 w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-lg hover:from-emerald-600 hover:to-emerald-500 transition-all duration-300 cursor-pointer shadow-md group-hover:shadow-lg"
                              style={{ height: `${height}%`, minHeight: '8px' }}
                              title={`₹${value.toLocaleString('en-IN')}`}
                            >
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-secondary-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                ₹{value.toLocaleString('en-IN')}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 text-xs text-secondary-500 font-medium text-center w-full">
                            {revenueChartData.labels[index] || ''}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="h-72 flex flex-col items-center justify-center text-secondary-400">
                    <BarChart3 className="h-16 w-16 mb-4 opacity-50" />
                    <p className="text-sm">No revenue data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Status Distribution */}
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-indigo-50 to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                      <PieChart className="h-5 w-5 mr-2 text-indigo-600" />
                      Invoice Status
                    </h3>
                    <p className="text-sm text-secondary-500 mt-1">Payment status distribution</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {invoiceStatusWithPercentages.length > 0 ? (
                  <div className="space-y-5">
                    {invoiceStatusWithPercentages.map((item, index) => {
                      const colors = [
                        { bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-700' },
                        { bg: 'bg-green-500', light: 'bg-green-50', text: 'text-green-700' },
                        { bg: 'bg-yellow-500', light: 'bg-yellow-50', text: 'text-yellow-700' },
                        { bg: 'bg-red-500', light: 'bg-red-50', text: 'text-red-700' },
                        { bg: 'bg-purple-500', light: 'bg-purple-50', text: 'text-purple-700' }
                      ]
                      const color = colors[index % colors.length]
                      return (
                        <div key={item.label} className="space-y-2">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full ${color.bg}`}></div>
                              <span className="text-sm font-medium text-secondary-900">{item.label}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-semibold text-secondary-900">{item.value}</span>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color.light} ${color.text}`}>
                                {item.percentage}%
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-secondary-100 rounded-full h-3 overflow-hidden">
                            <div 
                              className={`${color.bg} h-full rounded-full transition-all duration-500 ease-out shadow-sm`}
                              style={{ width: `${item.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="h-72 flex flex-col items-center justify-center text-secondary-400">
                    <PieChart className="h-16 w-16 mb-4 opacity-50" />
                    <p className="text-sm">No invoice data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customer Growth Chart */}
          {customerGrowthData.data && customerGrowthData.data.length > 0 && (
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-blue-50 to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                      <Activity className="h-5 w-5 mr-2 text-blue-600" />
                      Customer Growth
                    </h3>
                    <p className="text-sm text-secondary-500 mt-1">Customer acquisition over time</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="h-72 flex items-end justify-between space-x-1">
                  {customerGrowthData.data.map((value, index) => {
                    const maxValue = Math.max(...customerGrowthData.data)
                    const height = maxValue > 0 ? (value / maxValue) * 100 : 0
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center group">
                        <div className="w-full relative" style={{ height: '240px' }}>
                          <div 
                            className="absolute bottom-0 w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg hover:from-blue-600 hover:to-blue-500 transition-all duration-300 cursor-pointer shadow-md group-hover:shadow-lg"
                            style={{ height: `${height}%`, minHeight: '8px' }}
                            title={`${value} customers`}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-secondary-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              {value} customers
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 text-xs text-secondary-500 font-medium text-center w-full">
                          {customerGrowthData.labels[index] || ''}
                        </div>
                      </div>
                    )
                  })}
            </div>
          </div>
        </div>
          )}

          {/* Recent Activity Grid - Enhanced */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Recent Customers */}
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-blue-50 to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900">Recent Customers</h3>
                    <p className="text-sm text-secondary-500 mt-1">Latest registered</p>
                  </div>
                  <button
                    onClick={() => navigate('/customers')}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center hover:underline"
                  >
                    View all
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </button>
          </div>
              </div>
              <div className="p-4">
                {recentCustomers.length === 0 ? (
                  <div className="text-center py-12 text-secondary-400">
                    <Users className="mx-auto h-12 w-12 mb-3 opacity-50" />
                    <p className="text-sm font-medium">No customers yet</p>
                    <p className="text-xs mt-1">Get started by adding your first customer</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentCustomers.slice(0, limit).map((customer) => {
                      // Handle both API response formats
                      const customerName = customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unknown'
                      const customerPhone = customer.phone || ''
                      const customerEmail = customer.email || ''
                      const customerType = customer.type || customer.customerType || 'Regular'
                      const isActive = customer.isActive !== undefined ? customer.isActive : true
                      
                      return (
                        <div 
                          key={customer.id} 
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 cursor-pointer transition-all group border border-transparent hover:border-blue-200"
                          onClick={() => navigate(`/customers`)}
                        >
                          <div className="flex-shrink-0">
                            <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                              <Users className="h-6 w-6 text-white" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-secondary-900 truncate">
                              {customerName}
                            </p>
                            <p className="text-xs text-secondary-500 truncate mt-0.5">
                              {customerPhone || customerEmail || 'No contact'}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              isActive 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Measurements */}
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-purple-50 to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900">Recent Measurements</h3>
                    <p className="text-sm text-secondary-500 mt-1">Latest taken</p>
                  </div>
                  <button
                    onClick={() => navigate('/measurements')}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center hover:underline"
                  >
                    View all
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                {recentMeasurements.length === 0 ? (
                  <div className="text-center py-12 text-secondary-400">
                    <Ruler className="mx-auto h-12 w-12 mb-3 opacity-50" />
                    <p className="text-sm font-medium">No measurements yet</p>
                    <p className="text-xs mt-1">Start recording customer measurements</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentMeasurements.slice(0, limit).map((measurement) => (
                      <div 
                        key={measurement.id} 
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-purple-50 cursor-pointer transition-all group border border-transparent hover:border-purple-200"
                        onClick={() => navigate(`/measurements/${measurement.id}`)}
                      >
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                            <Ruler className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-secondary-900 truncate">
                            {measurement.customer?.firstName} {measurement.customer?.lastName}
                          </p>
                          <p className="text-xs text-secondary-500 mt-0.5">
                            {measurement.createdAt ? new Date(measurement.createdAt).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            measurement.isActive 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {measurement.isActive ? 'Active' : 'Inactive'}
                          </span>
                  </div>
                </div>
              ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Invoices */}
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-indigo-50 to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900">Recent Invoices</h3>
                    <p className="text-sm text-secondary-500 mt-1">Latest generated</p>
                  </div>
                  <button
                    onClick={() => navigate('/invoices')}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center hover:underline"
                  >
                    View all
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                {recentInvoices.length === 0 ? (
                  <div className="text-center py-12 text-secondary-400">
                    <FileText className="mx-auto h-12 w-12 mb-3 opacity-50" />
                    <p className="text-sm font-medium">No invoices yet</p>
                    <p className="text-xs mt-1">Create your first invoice</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentInvoices.slice(0, limit).map((invoice) => (
                      <div 
                        key={invoice.id} 
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-indigo-50 cursor-pointer transition-all group border border-transparent hover:border-indigo-200"
                        onClick={() => navigate(`/invoices`)}
                      >
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                            <FileText className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-secondary-900 truncate">
                            Invoice #{invoice.id}
                          </p>
                          <p className="text-xs font-medium text-emerald-600 mt-0.5">
                            ₹{parseFloat(invoice.totalAmount || 0).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            invoice.paymentStatus === 'Paid' ? 'bg-green-100 text-green-700' :
                            invoice.paymentStatus === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                            invoice.paymentStatus === 'Overdue' ? 'bg-red-100 text-red-700' :
                            'bg-secondary-100 text-secondary-700'
                          }`}>
                            {invoice.paymentStatus || 'Pending'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
          </div>
        </div>
      </div>

          {/* Quick Actions - Enhanced */}
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">Quick Actions</h3>
              <p className="text-sm text-secondary-500 mt-1">Common tasks and shortcuts</p>
        </div>
            <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <button
                  onClick={() => navigate('/customers')}
                  className="group flex flex-col items-center p-6 border-2 border-secondary-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all duration-300 hover:shadow-md"
                >
                  <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-secondary-900">Add Customer</p>
                  <p className="text-xs text-secondary-500 mt-1 text-center">Register new customer</p>
                </button>
                <button
                  onClick={() => navigate('/measurements')}
                  className="group flex flex-col items-center p-6 border-2 border-secondary-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all duration-300 hover:shadow-md"
                >
                  <div className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Ruler className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-secondary-900">Take Measurements</p>
                  <p className="text-xs text-secondary-500 mt-1 text-center">Record measurements</p>
                </button>
                <button
                  onClick={() => navigate('/invoices')}
                  className="group flex flex-col items-center p-6 border-2 border-secondary-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-300 hover:shadow-md"
                >
                  <div className="p-4 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-secondary-900">Create Invoice</p>
                  <p className="text-xs text-secondary-500 mt-1 text-center">Generate invoice</p>
                </button>
                <button
                  onClick={() => navigate('/invoices')}
                  className="group flex flex-col items-center p-6 border-2 border-secondary-200 rounded-xl hover:border-emerald-300 hover:bg-emerald-50 transition-all duration-300 hover:shadow-md"
                >
                  <div className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <CreditCard className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-secondary-900">View Payments</p>
                  <p className="text-xs text-secondary-500 mt-1 text-center">Check payments</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
